import {
  HTTP_INTERCEPTORS,
  HttpClient,
  HttpInterceptor,
} from "@angular/common/http";
import { Inject, Injectable, Optional } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { Company } from "src/models/company.model";
import { environment } from "src/environments/environment";
import { tap } from "rxjs/operators";
import { CacheInterceptor } from "src/interceptors/cache.interceptor";

@Injectable({
  providedIn: "root",
})
export class CompanyService {
  private companiesSubject: BehaviorSubject<Array<Company>>;
  public companies$: Observable<Array<Company>>;

  constructor(
    private http: HttpClient,
    @Optional()
    @Inject(HTTP_INTERCEPTORS)
    private interceptors: HttpInterceptor[]
  ) {
    this.companiesSubject = new BehaviorSubject<Array<Company>>([]);
    this.companies$ = this.companiesSubject.asObservable();
  }

  private get cacheInterceptor(): CacheInterceptor | null {
    if (!this.interceptors) return null;
    return this.interceptors.find(
      (i) => i instanceof CacheInterceptor
    ) as CacheInterceptor;
  }

  getCompanies(): Array<Company> {
    return this.companiesSubject.value;
  }

  setCompanies(companies: Array<Company>): void {
    this.companiesSubject.next(companies);
  }

  replace(company: Company) {
    const companies = this.companiesSubject.value;
    const index = companies.findIndex((c) => c.slug === company.slug);
    if (index !== -1) {
      companies[index] = company;
      console.log(
        `Invalidating cache for ${environment.apiUrl}company/${company.slug}/`
      );
      console.log(company.slug);
      this.cacheInterceptor?.invalidateCache(
        `${environment.apiUrl}company/${company.slug}/`
      );
      this.companiesSubject.next(companies);
    }
  }

  remove(company: Company) {
    const companies = this.companiesSubject.value;
    const index = companies.findIndex((c) => c.slug === company.slug);
    if (index !== -1) {
      companies.splice(index, 1);
      this.companiesSubject.next(companies);
    }
  }

  list(): Observable<Array<Company>> {
    return this.http.get(`${environment.apiUrl}/company/`) as Observable<
      Array<Company>
    >;
  }

  update(slug: string, data: object): Observable<Company> {
    return this.http.patch(`${environment.apiUrl}/company/${slug}/`, data).pipe(
      tap((updatedCompany: Company) => {
        this.replace(updatedCompany);
        // Mettre à jour le buyer si c'est l'entreprise courante
        const companies = this.companiesSubject.value;
        const index = companies.findIndex(
          (c) => c.slug === updatedCompany.slug
        );
        if (index !== -1) {
          companies[index] = updatedCompany as Company;
          this.companiesSubject.next(companies);
        }
      })
    ) as Observable<Company>;
  }

  create(data: object): Observable<Company> {
    return this.http.post(
      `${environment.apiUrl}/company/`,
      data
    ) as Observable<Company>;
  }

  delete(slug: string): Observable<Company> {
    return this.http.delete(
      `${environment.apiUrl}/company/${slug}/`
    ) as Observable<Company>;
  }

  supplierCompanies(): Observable<Array<Company>> {
    return this.http.get(
      `${environment.apiUrl}/company/supplier_companies/`
    ) as Observable<Array<Company>>;
  }

  get(slug: string): Observable<Company> {
    return this.http.get(`${environment.apiUrl}/company/${slug}/`).pipe(
      tap((updatedCompany: Company) => {
        this.replace(updatedCompany);
        // Mettre à jour le buyer si c'est l'entreprise courante
        const companies = this.companiesSubject.value;
        const index = companies.findIndex(
          (c) => c.slug === updatedCompany.slug
        );
        if (index !== -1) {
          companies[index] = updatedCompany as Company;
          this.companiesSubject.next(companies);
        }

        console.log(
          `Invalidating cache for ${environment.apiUrl}company/${updatedCompany.slug}/`
        );
        console.log(updatedCompany.slug);
        this.cacheInterceptor?.invalidateCache(
          `${environment.apiUrl}company/${updatedCompany.slug}/`
        );
        this.companiesSubject.next(companies);
      })
    ) as Observable<Company>;
  }
}
